import { isSome } from '@mbfab/shared'

import { notify } from '@/lib/notify'
import { userFacingErrorMessage } from '@/lib/user-facing-error-message'

// mutation を伴うボタンの「実行 + 結果フィードバック」を1箇所にまとめる共通ヘルパー。
// 押しても何も起きないように見える操作を無くすため、成功・失敗どちらでも必ずトーストを出す
// （docs/develop/client_ui/screens/states.md「操作の結果は「成功はトースト、失敗はトーストと画面の両方」」）。
//
// millsheet-home-container.tsx / shearing-home-container.tsx 等が持っていた
// 「mutation実行 → 一覧の再取得（失敗は握りつぶす）→ 画面側の結果 state 更新」というラッパーの
// 重複実装をここに集約する。
//
// 成功時は onResult を呼ばない（型からも 'success' を落としている）。ResultBanner はトーストと
// 同じ文言をそのまま画面に居座らせるだけで情報が増えず、数秒で自動的に消えるトーストと違って
// 手動で消すまで画面を占有し続けるため（2026/08 FB「結果表示パネルは基本消してほしい。toast で十分」）。
// 一方、失敗理由はコピーして読み直したい情報なのでパネルとして残す価値があり、onResult は
// 失敗時のみ呼ぶ
export type MutationFeedback = { type: 'error'; message: string }

// message = 何が起きたか（トーストの見出し）、description = 次に何をすればよいか
// （docs/develop/client_ui/components/feedback.md「notify（トースト）」）
type MutationMessage = { message: string; description?: string }

// 失敗トーストの既定の説明文。サーバーがユーザー向けの理由を返さなかったとき
// （ネットワーク断・内部エラー等）に「次の一手」として出す。
// 見出しと同じ文言を2回出しても情報が増えないため、ここで一律に持つ。
// 件数確認・印刷対象取得など runMutation を経由しない呼び出しの失敗時にも同じ文言を出したいため export する
// （画面側で同じ文言を再定義・直書きしない。millsheets-table.tsx / shearings-table.tsx / orders-table.tsx）
export const RETRY_HINT = '時間をおいて、もう一度お試しください。'

export async function runMutation({
  mutate,
  success,
  failure,
  refetch,
  onResult,
}: {
  // 例外を投げる mutation（`client.xxx()`）をそのまま渡せる。
  // route 側で既にエラーを握りつぶし「成否だけ boolean で返す」設計の口
  // （OrdersTable の onBulkExclude 等）に合わせ、**false に解決した場合も失敗として扱う**
  mutate: () => Promise<unknown>
  success: MutationMessage
  failure: MutationMessage
  // 成功後の一覧再取得（省略可）。失敗しても mutation 自体は成功しているため、
  // 「一覧が古いままになる」だけの成功扱いとし、握りつぶす（millsheet-home-container.tsx と同じ挙動）
  refetch?: () => Promise<unknown>
  // 省略可。既存の ResultBanner 用 state（setFeedback 等）をそのまま更新するためのフック
  onResult?: (result: MutationFeedback) => void
}): Promise<boolean> {
  // ResultBanner に残す message は具体的なサーバーエラー文言（無ければ failure.message）。
  // トーストの description は、サーバーが理由を返したならそれを、返さなかったなら「次の一手」を出す
  // （見出しと同じ文言を description に繰り返しても情報が増えないため）
  const fail = (error: unknown): false => {
    const resultMessage = userFacingErrorMessage(error, failure.message)
    const description = failure.description ?? (resultMessage === failure.message ? RETRY_HINT : resultMessage)
    notify.error(failure.message, description)
    onResult?.({ type: 'error', message: resultMessage })
    return false
  }

  try {
    if ((await mutate()) === false) {
      // boolean を返す口の失敗。例外ではないのでサーバー文言は取れず、RETRY_HINT に落ちる
      return fail(new Error(failure.message))
    }
  } catch (error) {
    return fail(error)
  }

  if (isSome(refetch)) {
    try {
      await refetch()
    } catch {
      // 操作自体は成功しているため、一覧が古いことだけを成功扱いのままにする
    }
  }

  notify.success(success.message, success.description)
  return true
}
